import React, { useEffect, useRef, useState } from 'react';
import { useApolloClient, NetworkStatus, gql } from '@apollo/client';

import { Box } from '@nerdwallet/currency/components/Box';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import { Text } from '@nerdwallet/currency/components/Text';

import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { getLogger } from '@lib/logging';
import { useIsNerdWalletPlusMember } from '@lib/hooks/nerdwallet-plus';

import { Actions } from '../canopy-flow-state';
import type { Action, PolicyToReview } from '../canopy-flow-state';

type FetchRatesScreenProps = {
  dispatch: React.Dispatch<Action>;
  policy: PolicyToReview;
};

const POLLING_ATTEMPTS = 3;
const POLLING_DELAY = 5000;
const MINIMUM_WAIT_DELAY = 4000;

const FETCH_RATE_REPORT_QUERY = gql`
  query FetchRateReport($policyId: ID!, $limit: Int) {
    insurancePolicyRateReport(policyId: $policyId, limit: $limit) {
      rateReport {
        rates {
          id
        }
      }
    }
  }
`;

const logger = getLogger('FetchRatesScreen');

const FetchRatesScreen = ({
  dispatch,
  policy,
}: FetchRatesScreenProps): React.ReactNode => {
  const apolloClient = useApolloClient();

  const initialRenderDateTime = useRef(new Date());
  const [isPolling, setIsPolling] = useState(true);

  const { isNerdWalletPlusMember, loading: nwPlusLoading } =
    useIsNerdWalletPlusMember();

  const [remainingAttempts, setRemainingAttempts] = useState(
    POLLING_ATTEMPTS - 1, // Subtract one to account for the first *non-polling* fetch
  );

  const [prevNetworkStatus, setPrevNetworkStatus] =
    useState<NetworkStatus>(null);

  const { data, stopPolling, networkStatus } = useQueryWithLog(
    logger,
    FETCH_RATE_REPORT_QUERY,
    {
      variables: { policyId: policy?.id, limit: 5 },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      pollInterval: POLLING_DELAY,
    },
  );

  const availableRates =
    data?.insurancePolicyRateReport?.rateReport?.rates ?? [];

  const hasRates = availableRates.length > 0;

  useEffect(
    function decrementRemainingAttempts() {
      setPrevNetworkStatus(networkStatus);

      if (
        prevNetworkStatus !== NetworkStatus.poll &&
        networkStatus === NetworkStatus.poll
      ) {
        setRemainingAttempts(remainingAttempts - 1);
      }
    },
    [networkStatus, prevNetworkStatus, remainingAttempts],
  );

  useEffect(
    function checkIfPollingShouldStop() {
      if (remainingAttempts <= 0 || hasRates) {
        setIsPolling(false);
        stopPolling();
      }
    },
    [hasRates, remainingAttempts, stopPolling],
  );

  useEffect(() => {
    if (nwPlusLoading) {
      return;
    }

    if (hasRates) {
      const currentDateTime = new Date();

      const currentTime = currentDateTime.getTime();
      const adjustedTime =
        initialRenderDateTime.current.getTime() + MINIMUM_WAIT_DELAY;

      const delayTimeout = Math.max(0, adjustedTime - currentTime);

      const delayedCheckForRates = setTimeout(() => {
        dispatch({
          type: Actions.CHECK_POLICIES_AND_RATES,
          payload: {
            hasRateReport: true,
            isNerdWalletPlusMember,
            isFreeRateMember: !isNerdWalletPlusMember,
          },
        });
      }, delayTimeout);

      apolloClient.refetchQueries({
        updateCache(cache) {
          cache.evict({ fieldName: 'insurancePoliciesRateReports' });
        },
      });

      return () => {
        if (delayedCheckForRates) clearTimeout(delayedCheckForRates);
      };
    }

    if (!isPolling && !hasRates) {
      dispatch({
        type: Actions.CHECK_POLICIES_AND_RATES,
        payload: {
          policyNeedsReview: true,
          isNerdWalletPlusMember,
        },
      });
    }
  }, [
    isPolling,
    hasRates,
    policy,
    dispatch,
    apolloClient,
    nwPlusLoading,
    isNerdWalletPlusMember,
  ]);

  return (
    <Box
      display="flex"
      alignContent="center"
      alignItems="center"
      flexDirection="column"
    >
      <Box marginBottom="2">
        <LoadingSpinner inline size={64} />
      </Box>
      <Box marginBottom="1">
        <Text align="center" component="p">
          Analyzing your rates
        </Text>
      </Box>
      <Text align="center" component="p" tone="light" size="x-small">
        Do not refresh this page
      </Text>
    </Box>
  );
};

export default FetchRatesScreen;
